import type { Provenance } from "@/lib/comms";
import { cn } from "@/lib/utils";

const LABEL: Record<Provenance, { short: string; full: string }> = {
  real: { short: "REAL", full: "REAL DATA" },
  mock: { short: "MOCK", full: "MOCK DATA" },
};

interface ProvenanceBadgeProps {
  provenance: Provenance;
  /** Full "REAL DATA"/"MOCK DATA" wording — the demo legend key uses this;
   *  inline badges on data rows default to the short form. */
  full?: boolean;
  className?: string;
}

/**
 * Real-vs-mock data indicator. Two sizes:
 *
 * - `full` (the DEMO LEGEND key) — a prominent pill: filled brand-red for
 *   `real`, muted outline for `mock`.
 * - inline (default, on data rows) — a deliberately tiny hint: a small colour
 *   dot plus a micro label, so it reads as a subtle annotation rather than
 *   competing with the row content.
 *
 * Badge-only — never filters anything, and the text label carries the meaning
 * too (not colour alone; the dot has an accessible title).
 */
export function ProvenanceBadge({ provenance, full = false, className }: ProvenanceBadgeProps) {
  if (full) {
    return (
      <span
        className={cn(
          "inline-flex w-fit shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-[0.04em] whitespace-nowrap uppercase",
          provenance === "real"
            ? "bg-jj-red text-white"
            : "border-wg-04 text-wg-06 border bg-white",
          className,
        )}
      >
        {LABEL[provenance].full}
      </span>
    );
  }
  return (
    <span
      title={LABEL[provenance].full}
      className={cn(
        "inline-flex w-fit shrink-0 items-center gap-1 text-[9px] font-medium tracking-[0.04em] whitespace-nowrap uppercase",
        provenance === "real" ? "text-jj-red" : "text-wg-06",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn("h-1.5 w-1.5 rounded-full", provenance === "real" ? "bg-jj-red" : "bg-wg-04")}
      />
      {LABEL[provenance].short}
    </span>
  );
}
